import { Component, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { GatewayOrder } from '../../models/GatewayOrder';

@Component({
  selector: 'app-order-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule
  ],
  templateUrl: './order-search.component.html',
  styleUrl: './order-search.component.css'
})
export class OrderSearchComponent implements OnDestroy {
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.search-wrapper');

    if (!clickedInside) {
      this.closeResults();
    }
  }
  searchValue: string = '';
  searchResults: GatewayOrder[] = [];
  isLoading: boolean = false;
  showResults: boolean = false;
  private lastSearchedValue: string = '';
  private baseUrl = `${environment.backend.baseUrl}${environment.backend.apiUrl}/`;
  private hideTimeout: any = null;
  private isInputFocused: boolean = false;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Only allow numbers
    value = value.replace(/\D/g, '');

    // Update the input value
    this.searchValue = value;
    input.value = value;

    // If at least 3 digits and different from last search, trigger the API call
    if (value.length >= 3 && value !== this.lastSearchedValue) {
      this.lastSearchedValue = value;
      this.triggerSearch(value);
    } else if (value.length < 3) {
      // Hide results if less than 3 digits
      this.showResults = false;
      this.searchResults = [];
    }
  }

  private triggerSearch(searchTerm: string): void {
    this.isLoading = true;
    this.showResults = false;

    this.http.get<GatewayOrder[]>(`${this.baseUrl}Gateway/orders/like/${searchTerm}`)
      .subscribe({
        next: (results) => {
          this.searchResults = results;
          this.showResults = true;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Search error:', error);
          this.searchResults = [];
          this.showResults = false;
          this.isLoading = false;
        }
      });
  }

  navigateToCustomer(customerNo: number): void {
    this.router.navigate(['/admin/customers/details', customerNo]);
    this.showResults = false;
    this.searchValue = '';
    this.lastSearchedValue = '';
  }

  navigateToOrder(orderId: string): void {
    this.router.navigate(['/admin/customers/orders', orderId]);
    this.showResults = false;
    this.searchValue = '';
    this.lastSearchedValue = '';
  }

  onMouseLeave(): void {
    // Only start timer if input is not focused (user is not actively typing)
    if (!this.isInputFocused) {
      this.hideTimeout = setTimeout(() => {
        this.showResults = false;
      }, 1000);
    }
  }

  onMouseEnter(): void {
    // Cancel the hide timer if mouse re-enters
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  onInputFocus(): void {
    this.isInputFocused = true;
    // Cancel any pending hide timer when input is focused
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  onInputBlur(): void {
    this.isInputFocused = false;
    // Start hide timer when input loses focus (after a small delay)
    setTimeout(() => {
      if (!this.isInputFocused) {
        this.hideTimeout = setTimeout(() => {
          this.showResults = false;
        }, 1000);
      }
    }, 100);
  }

  closeResults(): void {
    this.showResults = false;
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
